import React from "react";
import { Box, Dialog, LinearProgress, Typography } from "@mui/material";
import Button from "../inputs/Button";
import { colors } from "../../../styles/colors";
import { fonts } from "../../../styles/theme";

/**
 * Drehender Rotorstern als Ladeanzeige – die hauseigene Alternative zum Kreis-Spinner.
 *
 * Der Rotor stammt aus `public/icons/windrad2.svg` und steht hier als Inline-SVG, damit er
 * `currentColor` erbt; als <img> ginge das nicht. Die Nabe sitzt im Original bei (12,10) und
 * nicht in der Mitte der viewBox – ohne die verschobene transform-origin würde der Stern
 * eiern statt sich zu drehen.
 */
const WindradSpinner: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    role="progressbar"
    aria-label="Berechnung läuft"
    sx={{
      width: 64,
      height: 64,
      color: colors.brand.primary,
      transformOrigin: "50% 41.67%",
      animation: "windrad-drehung 3s linear infinite",
      "@keyframes windrad-drehung": {
        from: { transform: "rotate(0deg)" },
        to: { transform: "rotate(360deg)" },
      },
      // Wer Bewegung im Betriebssystem abgeschaltet hat, bekommt den Rotor still gestellt.
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    }}
  >
    <path
      d="M12 4v6m0 0l-4 4m4-4l4 4"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

interface ProgressDialogProps {
  /** Steuert, ob der Dialog geöffnet ist */
  open: boolean;
  /** Überschrift, z.B. "Projekte werden neu berechnet" */
  titel: string;
  /** Nummer des laufenden Schritts, 1-basiert */
  aktuell: number;
  /** Gesamtzahl der Schritte */
  gesamt: number;
  /** Bezeichnung des laufenden Schritts, z.B. der Projektname */
  label?: string;
  /** Ohne Angabe erscheint kein Abbrechen-Button */
  onAbbrechen?: () => void;
}

/**
 * Blockierendes Fortschritts-Popup für längere, schrittweise Abläufe.
 *
 * Nimmt ausschließlich fertige Werte entgegen und kennt weder Projekte noch API-Aufrufe –
 * die Schleife gehört dem Aufrufer (siehe `hooks/useSequentiellerLauf`).
 *
 * Absichtlich nicht über Backdrop oder Escape schließbar: Der Ablauf läuft weiter, auch wenn
 * das Popup verschwindet. Wer ihn beenden will, nimmt den Abbrechen-Button.
 */
const ProgressDialog: React.FC<ProgressDialogProps> = ({
  open,
  titel,
  aktuell,
  gesamt,
  label,
  onAbbrechen,
}) => {
  const prozent = gesamt > 0 ? (aktuell / gesamt) * 100 : 0;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 2 } } }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 2,
          px: 4,
          py: 5,
        }}
      >
        <WindradSpinner />

        <Typography variant="h6" component="p">
          {titel}
        </Typography>

        {/* Feste Höhe: Die Bezeichnung wechselt bei jedem Schritt, ohne sie würde der
            Dialog bei unterschiedlich langen Namen zappeln. */}
        <Box sx={{ width: "100%", minHeight: 24 }}>
          <Typography
            sx={{
              color: colors.text.primary,
              fontWeight: fonts.weights.medium,
            }}
          >
            {label}
            {gesamt > 0 && (
              <Box component="span" sx={{ color: colors.text.secondary }}>
                {label ? " " : ""}({aktuell} von {gesamt})
              </Box>
            )}
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={prozent}
          sx={{ width: "100%", height: 6, borderRadius: 3 }}
        />

        {onAbbrechen && (
          <Button variant="secondary" size="small" onClick={onAbbrechen}>
            Abbrechen
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

export default ProgressDialog;
